/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    arrayOf, bool, func, shape, string,
} from 'prop-types';
import {
    Accordion, AccordionDetails, AccordionSummary,
} from '@material-ui/core';
import ReactMarkdown from 'react-markdown';

import { useUIDSeed } from 'react-uid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Add, Remove } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    faqMainContainer: {
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto 2em auto',
        [theme.breakpoints.down(1401)]: {
            padding: '0px 12px',
        },
    },
    faqTitle: {
        fontSize: '2.5em',
        [theme.breakpoints.down(860)]: {
            fontSize: '2em',
        },
        [theme.breakpoints.down(380)]: {
            fontSize: '1.8em',
        },
        textAlign: 'center',
        fontFamily: theme.typography?.heading?.fontFamily,
        paddingBottom: '10px',
        textWrap: 'balance',
    },
    questionContainer: {
        fontSize: '1.2em',
        fontFamily: theme.typography?.fontFamily,
        [theme.breakpoints.down(680)]: {
            fontSize: '1.1em',
        },
    },
    accordionContainer: {
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.primary.main || '#2F2F2F'}`,
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '10px 0px',
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
            borderBottom: `2px solid ${theme.palette.primary.main || '#2F2F2F'}`,
            minHeight: '54px',
        },
        '& .MuiAccordionSummary-expandIcon.Mui-expanded': {
            '& $addIcon': {
                clip: 'rect(0 0 0 0)',
                overflow: 'hidden',
                position: 'absolute',
            },
        },
        '& .MuiAccordionSummary-root .MuiAccordionSummary-expandIcon': {
            '& $removeIcon': {
                clip: 'rect(0 0 0 0)',
                overflow: 'hidden',
                position: 'absolute',
            },
        },
        '&.MuiAccordion-root.Mui-expanded': {
            borderBottom: 'none',
            margin: '0px',
        },
        '&.MuiAccordion-root': {
            '&::before': {
                content: 'none',
            },
        },
        '& .MuiAccordionSummary-root .MuiAccordionSummary-expandIcon.Mui-expanded': {
            '& $removeIcon': {
                clip: 'rect(0 0 0 0)',
                overflow: 'auto',
                position: 'static',
            },
        },
    },
    detailsContainer: {
        padding: '15px 0px 8px 0px',
    },
    answerContainer: {
        fontFamily: theme.typography?.fontFamily,
        fontSize: '1em',
        '& a': {
            color: theme.palette?.primary?.main || '#2F2F2F',
        },
    },
    summaryContainer: {
        padding: '0px',
        position: 'relative',
    },
    faqTitleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },
    removeIcon: {
        border: `1px solid ${theme.palette.primary.main || '#2F2F2F'}`,
        borderRadius: '50%',
        background: theme.palette.bgContrastPrimaryColor || '#E8F1ED',
    },
    addIcon: {
        border: `1px solid ${theme.palette.primary.main || '#2F2F2F'}`,
        borderRadius: '50%',
    },

}));

const FAQBlockV2 = ({
    faqBlock, displayCsTitle, handleTrackEvent, currentPage,
}) => {
    const classes = useStyles();
    const isMobileDevice = useMediaQuery('(max-width: 767px)');
    const seed = useUIDSeed();

    // use for added the pre text on the FAQ title.
    const titleHeading = {
        desktop: 'Frequently Asked Questions About ',
        mobile: 'FAQ About ',
    };

    const handleToggle = (ques) => {
        handleTrackEvent(currentPage || 'Subscriptions Experience', 'FAQs', ques || '');
    };

    // render the title different based on devices
    let mainFAQTitle = isMobileDevice ? `${titleHeading?.mobile}${faqBlock?.title}` : `${titleHeading?.desktop}${faqBlock?.title}`;
    if (displayCsTitle) mainFAQTitle = faqBlock?.title;

    // using contentstack to handle expanded/collapsed logic
    const setDefaultExpand = (item, index) => {
        if (Object.keys(item)?.length > 0 && Object.keys(item).includes('isexpanded')) return item.isexpanded;
        return `panel_${index}` === 'panel_0';
    };

    return (
        <div className={classes.faqMainContainer}>
            {mainFAQTitle && <div className={`${classes.faqTitle} _main_faq_title`}>{mainFAQTitle} </div>}
            {
                faqBlock?.faqs?.map((itemFaq, index) => (
                    <div key={seed(itemFaq)}>
                        <Accordion
                            square
                            className={classes.accordionContainer}
                            defaultExpanded={setDefaultExpand(faqBlock, index)}
                            onChange={(e, expanded) => {
                                if (expanded) handleToggle(itemFaq?.question);
                            }}
                        >
                            <AccordionSummary
                                className={classes.summaryContainer}
                                aria-controls={`panel-header${index}`}
                                expandIcon={(
                                    <>
                                        <Remove className={classes.removeIcon} />
                                        <Add className={classes.addIcon} />
                                    </>
                                )}
                            >
                                <div className={classes.faqTitleContainer}>
                                    <div className={classes.questionContainer}>
                                        {itemFaq?.question}
                                    </div>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails className={classes.detailsContainer}>
                                <div className={classes.answerContainer}>
                                    <ReactMarkdown source={itemFaq?.answer} skipHtml={false} />
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                ))
            }
        </div>
    );
};

FAQBlockV2.propTypes = {
    faqBlock: shape({
        title: string,
        faqs: arrayOf(shape({
            answer: string,
            question: string,
        })),
    }),
    displayCsTitle: bool,
    handleTrackEvent: func,
    currentPage: string,
};

FAQBlockV2.defaultProps = {
    faqBlock: {},
    displayCsTitle: false,
    handleTrackEvent: () => { },
    currentPage: '',
};

export default FAQBlockV2;
